import { getResource } from "../language/resources/getResource";
import { formatTimeOnly } from "../language/time";
import { Resort } from "../resortDetails/resort";
import { AccessNode } from "./models";
import { AccessOverviewQuery } from "./models";
import { ItemKey } from "./multiSelect/types";

export const resortToAccessNode = (r: Resort): AccessNode => ({
  type: "resort",
  id: `resort:${r.resortId}`,
  name:
    r.resortName?.trim() || r.resortAbbreviation?.trim() || "(Unnamed resort)",
  raw: r,
});

export const compareByNameAsc = (a: AccessNode, b: AccessNode) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

export function extractIds(keys: ItemKey[]): number[] {
  return keys
    .map((key) => {
      if (typeof key === "number") return key;

      // key is string here — extract FIRST numeric sequence
      const match = key.match(/\d+/);
      return match ? Number(match[0]) : undefined;
    })
    .filter((n): n is number => Number.isFinite(n));
}

export function extractId(key: ItemKey): number | undefined {
  if (typeof key === "number") return key;

  const match = key.match(/\d+/);
  return match ? Number(match[0]) : undefined;
}

const weekdayResourceKeys: Record<number, string> = {
  0: "DAY_OF_WEEK_SUN",
  1: "DAY_OF_WEEK_MON",
  2: "DAY_OF_WEEK_TUE",
  3: "DAY_OF_WEEK_WED",
  4: "DAY_OF_WEEK_THU",
  5: "DAY_OF_WEEK_FRI",
  6: "DAY_OF_WEEK_SAT",
};

export const getWeekdayLabel = (
  dayOfWeekId: number,
  resources: any,
  locale: string = navigator.language,
): string => {
  const key = weekdayResourceKeys[dayOfWeekId];
  const resourceLabel = getResource(resources, key);

  if (resourceLabel) {
    return resourceLabel;
  }

  // Default: localized short weekday name
  const baseDate = new Date(Date.UTC(2020, 0, 5 + dayOfWeekId));
  return new Intl.DateTimeFormat(locale, { weekday: "short" }).format(baseDate);
};

/**
 * Formats a start/end time range using formatTimeOnly.
 * Returns undefined if either time is invalid or missing.
 */
export const formatTimeRange = (
  startTime?: string,
  endTime?: string,
  timeFormat: string = "h:mm a",
  shortVersion: boolean = true,
): string | undefined => {
  if (!startTime || !endTime) return undefined;

  const formattedStart = formatTimeOnly(startTime, timeFormat, shortVersion);
  const formattedEnd = formatTimeOnly(endTime, timeFormat, shortVersion);

  if (!formattedStart || !formattedEnd) return undefined;

  return `${formattedStart} – ${formattedEnd}`;
};

export const normalizeFilter = (filter: AccessOverviewQuery) => {
  const sort = (arr?: number[]) =>
    arr ? [...arr].sort((a, b) => a - b) : undefined;

  return JSON.stringify({
    ...filter,
    sellingSeasonIds: sort(filter.sellingSeasonIds),
    fulfillmentSeasonIds: sort(filter.fulfillmentSeasonIds),
    resortIds: sort(filter.resortIds),
    entitlementTypeIds: sort(filter.entitlementTypeIds),
  });
};

export const denormalizeFilter = (json: string): AccessOverviewQuery => {
  const obj = JSON.parse(json);

  return {
    ...obj,
    sellingSeasonIds: obj.sellingSeasonIds ?? undefined,
    fulfillmentSeasonIds: obj.fulfillmentSeasonIds ?? undefined,
    resortIds: obj.resortIds ?? undefined,
    entitlementTypeIds: obj.entitlementTypeIds ?? undefined,
  } as AccessOverviewQuery;
};

export interface MultiLineFormatOptions {
  maxLines?: number; // default 3
  separator?: string; // default "\n"
}

/**
 * Formats an array of values into a string with line breaks (or custom separator),
 * limited to a maximum number of lines if maxLines is provided.
 *
 * If the array exceeds maxLines, returns maxLines items followed by "…".
 */
export function formatLimitedMultiLine(
  values: (string | undefined)[],
  options: MultiLineFormatOptions = {},
): string | undefined {
  const { maxLines = 3, separator = "\n" } = options;

  const filtered = values.filter(Boolean) as string[];

  if (filtered.length === 0) return undefined;
  if (filtered.length <= maxLines) return filtered.join(separator);

  return [...filtered.slice(0, maxLines), "..."].join(separator);
}

/**
 * Formats ALL values into a multi-line string using the separator.
 */
export function formatAllMultiLine(
  values: (string | undefined)[],
  options: MultiLineFormatOptions = {},
): string | undefined {
  const { separator = "\n" } = options;

  const filtered = values.filter(Boolean) as string[];
  return filtered.length > 0 ? filtered.join(separator) : undefined;
}

/**
 * Format all values into a single line string using the separator,
 * or return undefined if no valid values.
 */
export function formatOneLine(
  values: (string | undefined)[],
  separator: string = ", ",
): string | undefined {
  const filtered = values.filter(Boolean) as string[];
  return filtered.length > 0 ? filtered.join(separator) : undefined;
}

export function formatRuleIds(
  rules: { liftAccessRuleId?: number | null }[],
): string {
  return rules
    .map((rule) => rule.liftAccessRuleId)
    .filter((id): id is number => id != null)
    .join(", ");
}

export const RESORT_EXCLUSIONS = {
  ACCESS_OVERVIEW: ["alta-snowbird ski area - historical"],

  ACCESS_RULE_CREATOR: ["alta-snowbird ski area - historical", "all resorts"],
} as const;

export function shouldIncludeResort(
  resort: { resortName?: string },
  excludedPrefixes: readonly string[],
): boolean {
  const name = resort?.resortName?.toLowerCase();
  if (!name) return true;

  return !excludedPrefixes.some((prefix) => name.startsWith(prefix));
}

export const createEmptyAccessOverviewQuery = (): AccessOverviewQuery => ({
  sellingSeasonIds: [],
  fulfillmentSeasonIds: [],
  resortIds: [],
  entitlementTypeIds: [],
  entitlementDescriptionFilter: "",
});
